import { ContactId } from '@disco/types/types/list'
import api from './server'
import type {
  Brief,
  BriefId,
  BriefNote,
  BriefRecipient,
  BriefRecipientContact,
  BriefProject,
  BriefProjectId,
  BriefPublishingStatus,
} from 'types/brief'

export const getBrief = (briefId: BriefId): Promise<Brief> =>
  api.getUrl(`${api.host}/api/briefs/${briefId}/`, null, 'api.getBrief')

export const getBriefVersion = (versionId: number): Promise<Brief> =>
  api.getUrl(
    `${api.host}/api/briefs/version/${versionId}/`,
    null,
    'api.getBriefVersion',
  )

export const updateBrief = (
  briefId: BriefId,
  brief: Partial<Omit<Brief, 'project'> & { project?: number }>,
): Promise<Brief> =>
  api.patchUrl(`${api.host}/api/briefs/${briefId}/`, brief, 'api.updateBrief')

export const deleteBrief = (briefId: BriefId): Promise<void> =>
  api.delUrl(`${api.host}/api/briefs/${briefId}/`, 'api.deleteBrief')

export const createBrief = (brief: Partial<Brief>): Promise<Brief> =>
  api.postUrl(`${api.host}/api/briefs/`, brief, 'api.createBrief')

export type BriefPublishResponse = {
  brief_version_id: number
  message: string
  publishing_status: BriefPublishingStatus
}

export const publishBrief = (briefId: BriefId): Promise<BriefPublishResponse> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/publish/`,
    null,
    'api.publishBrief',
  )

export const sendBrief = (
  briefId: BriefId,
  recipientsList?: { recipients_list: BriefRecipient[] } | null,
): Promise<{ scheduled_count: number }> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/send/`,
    recipientsList,
    'api.sendBrief',
  )

export const duplicateBrief = (briefId: BriefId): Promise<Brief> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/duplicate/`,
    null,
    'api.duplicateBrief',
  )

// Brief Project-related API functions

export const createBriefProject = (name: string): Promise<BriefProject> =>
  api.postUrl(
    `${api.host}/api/briefs/projects/`,
    { name },
    'api.createBriefProject',
  )

export const listBriefProjects = (): Promise<BriefProject[]> =>
  api.getUrl(`${api.host}/api/briefs/projects/`, null, 'api.listBriefProjects')

export const deleteBriefProject = (
  briefProjectId: BriefProjectId,
): Promise<void> =>
  api.delUrl(
    `${api.host}/api/briefs/projects/${briefProjectId}/`,
    'api.deleteBriefProject',
  )

export const getBriefProject = (
  briefProjectId: BriefProjectId,
): Promise<BriefProject> =>
  api.getUrl(
    `${api.host}/api/briefs/projects/${briefProjectId}/`,
    null,
    'api.getBriefProject',
  )

export const updateBriefProject = (
  briefProjectId: BriefProjectId,
  updates: Partial<BriefProject>,
): Promise<BriefProject> =>
  api.patchUrl(
    `${api.host}/api/briefs/projects/${briefProjectId}/`,
    updates,
    'api.updateBriefProject',
  )

export const getBriefsByBriefProject = (
  briefProjectId: BriefProjectId,
): Promise<Brief[]> =>
  api.getUrl(
    `${api.host}/api/briefs/?project_id=${briefProjectId}`,
    null,
    'api.getBriefsByBriefProject',
  )

export const moveBriefsToBriefProject = (
  briefIds: number[],
  briefProjectId: BriefProjectId | null,
): Promise<Brief[]> =>
  api.postUrl(
    `${api.host}/api/briefs/alter_project/`,
    { brief_ids: briefIds, project: briefProjectId },
    'api.moveBriefsToBriefProject',
  )

export const getBriefRecipients = (
  briefId: BriefId,
): Promise<BriefRecipientContact[]> =>
  api.getUrl(
    `${api.host}/api/briefs/${briefId}/recipients/`,
    null,
    'api.getBriefRecipients',
  )

export const toggleBriefLink = (
  briefId: BriefId,
  params: {
    recipients: Array<{
      contact_id: number
      saved_recipients_id: number | null
      enabled: boolean
    }>
  },
): Promise<void> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/toggle_link/`,
    params,
    'api.toggleBriefLink',
  )

export const discardBriefChanges = (briefId: BriefId): Promise<void> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/discard_changes/`,
    null,
    'api.discardBriefChanges',
  )

export type ShareBriefPayload = {
  recipients: BriefRecipient[]
  subject: string
  reply_to?: string
  sender_name?: string
  message?: string
}

export type ShareBriefResponse = {
  message: string
  brief_version_id: number
  brief_version_sent_id: number
  brief_version_name: string
  shared_with: Array<{
    id: ContactId
    email: string
    name: string
  }>
  subject: string
  reply_to: string
}

export const shareBrief = (
  briefId: BriefId,
  payload: ShareBriefPayload,
): Promise<ShareBriefResponse> =>
  api.postUrl(
    `${api.host}/api/briefs/${briefId}/share/`,
    payload,
    'api.shareBrief',
  )

// Brief Note-related API functions

export type CreateBriefNotePayload = {
  note_text: string
  for_brief?: BriefId
  for_project?: BriefProjectId
}

export type UpdateBriefNotePayload = {
  note_text?: string
  for_brief?: BriefId
  for_project?: BriefProjectId
}

export const createBriefNote = (
  payload: CreateBriefNotePayload,
): Promise<BriefNote> =>
  api.postUrl(`${api.host}/api/briefs/note/`, payload, 'api.createBriefNote')

export const updateBriefNote = (
  noteId: number,
  payload: UpdateBriefNotePayload,
): Promise<BriefNote> =>
  api.patchUrl(
    `${api.host}/api/briefs/note/${noteId}/`,
    payload,
    'api.updateBriefNote',
  )

export const deleteBriefNote = (noteId: number): Promise<void> =>
  api.delUrl(`${api.host}/api/briefs/note/${noteId}/`, 'api.deleteBriefNote')
